import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Button, CircularProgress, Alert, IconButton } from '@mui/material';
import { collection, getDocs, writeBatch, doc, deleteDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SeasonBreadcrumb from '../../../components/admin/SeasonBreadcrumb';
import { useSeason } from '../../../context/SeasonContext';
import { useNotification } from '../../../context/NotificationContext';

const positions = ['coach', 'keeper', 'defender', 'midfielder', 'attacker', 'team'];
const positionLabels = {
    coach: 'Coach',
    keeper: 'Keeper',
    defender: 'Verdediger',
    midfielder: 'Middenvelder',
    attacker: 'Aanvaller',
    team: 'Team'
};

const newRuleInitialState = { id: '', action: '', coach: 0, keeper: 0, defender: 0, midfielder: 0, attacker: 0, team: 0 };

function AdminPuntentelling() {
    // scoringRules is één globale collectie, niet per seizoen. Het seizoen uit de route
    // dient hier alleen voor het kruimelpad en de waarschuwing hieronder.
    const { isLocked } = useSeason();
    const notify = useNotification();

    const [rules, setRules] = useState([]);
    const [newRule, setNewRule] = useState(newRuleInitialState);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const fetchRules = useCallback(async () => {
        setLoading(true);
        try {
            const rulesCollection = collection(db, 'scoringRules');
            const snapshot = await getDocs(rulesCollection);
            const rulesData = snapshot.docs.map(doc => ({ ...doc.data() }));
            rulesData.sort((a, b) => (a.order || 0) - (b.order || 0));
            setRules(rulesData);
        } catch (err) {
            notify.error('Fout bij het ophalen van de puntentelling.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => {
        fetchRules();
    }, [fetchRules]);

    const handleValueChange = (ruleId, field, value) => {
        const newRules = rules.map(rule => {
            if (rule.id === ruleId) {
                const newValue = (field !== 'action') ? Number(value) || 0 : value;
                return { ...rule, [field]: newValue };
            }
            return rule;
        });
        setRules(newRules);
    };

    const handleNewRuleChange = (field, value) => {
        const newValue = (field !== 'action' && field !== 'id') ? Number(value) || 0 : value;
        setNewRule({ ...newRule, [field]: newValue });
    };

    const handleAddRule = async () => {
        if (!newRule.id || !newRule.action) {
            notify.error('ID en Actie zijn verplicht voor een nieuwe regel.');
            return;
        }
        if (rules.some(rule => rule.id === newRule.id)) {
            notify.error('Dit ID bestaat al. Kies een uniek ID.');
            return;
        }
        setSaving(true);
        try {
            const newRuleWithOrder = { ...newRule, order: rules.length };
            await setDoc(doc(db, 'scoringRules', newRule.id), newRuleWithOrder);
            setNewRule(newRuleInitialState);
            await fetchRules();
        } catch (err) {
            notify.error('Fout bij het toevoegen van de regel.');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteRule = async (ruleId) => {
        setSaving(true);
        try {
            await deleteDoc(doc(db, 'scoringRules', ruleId));
            await fetchRules();
        } catch (err) {
            notify.error('Fout bij het verwijderen van de regel.');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const handleReorder = (ruleId, direction) => {
        const rulesCopy = [...rules];
        const currentIndex = rulesCopy.findIndex(r => r.id === ruleId);

        if (currentIndex === -1) return;

        const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

        if (targetIndex < 0 || targetIndex >= rulesCopy.length) {
            return;
        }

        const currentOrder = rulesCopy[currentIndex].order;
        rulesCopy[currentIndex].order = rulesCopy[targetIndex].order;
        rulesCopy[targetIndex].order = currentOrder;

        rulesCopy.sort((a, b) => (a.order || 0) - (b.order || 0));
        setRules(rulesCopy);
    };

    const handleSaveChanges = async () => {
        setSaving(true);
        try {
            const batch = writeBatch(db);
            const rulesCollection = collection(db, 'scoringRules');
            rules.forEach(rule => {
                if (rule.id) {
                    const docRef = doc(rulesCollection, rule.id);
                    batch.set(docRef, rule);
                }
            });
            await batch.commit();
            await fetchRules();
        } catch (err) {
            notify.error('Fout bij het opslaan van de wijzigingen.');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <CircularProgress />;
    }

    return (
        <Box>
            <SeasonBreadcrumb page="Puntentelling" />
            <Typography variant="h4" gutterBottom>Puntentelling</Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
                Eén puntentelling voor het hele spel: elke subleague in elk seizoen rekent hiermee.
                Een wijziging telt met terugwerkende kracht mee zodra punten opnieuw berekend worden.
            </Typography>
            {isLocked && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Je bekijkt dit vanuit een afgerond seizoen. De puntentelling is niet per seizoen
                    vastgelegd — wijzig je hier iets, dan raakt dat ook de lopende competities.
                </Alert>
            )}
            <TableContainer component={Paper}>
                <Table aria-label="scoring rules admin table">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: '120px' }}>Volgorde</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>ID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Actie</TableCell>
                            {positions.map(pos => <TableCell key={pos} align="right" sx={{ fontWeight: 'bold' }}>{positionLabels[pos]}</TableCell>)}
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rules.map((rule, index) => (
                            <TableRow key={rule.id}>
                                <TableCell>
                                    <IconButton onClick={() => handleReorder(rule.id, 'up')} disabled={index === 0}>
                                        <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton onClick={() => handleReorder(rule.id, 'down')} disabled={index === rules.length - 1}>
                                        <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                                <TableCell component="th" scope="row">{rule.id}</TableCell>
                                <TableCell>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        value={rule.action}
                                        onChange={(e) => handleValueChange(rule.id, 'action', e.target.value)}
                                    />
                                </TableCell>
                                {positions.map(pos => (
                                    <TableCell key={pos} align="right">
                                        <TextField
                                            type="number"
                                            size="small"
                                            variant="outlined"
                                            value={rule[pos]}
                                            onChange={(e) => handleValueChange(rule.id, pos, e.target.value)}
                                            sx={{ width: '80px' }}
                                        />
                                    </TableCell>
                                ))}
                                <TableCell>
                                    <IconButton onClick={() => handleDeleteRule(rule.id)} color="error">
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {/* New Rule Row */}
                        <TableRow>
                            <TableCell />
                            <TableCell>
                                <TextField
                                    size="small"
                                    variant="outlined"
                                    value={newRule.id}
                                    onChange={(e) => handleNewRuleChange('id', e.target.value)}
                                    placeholder="uniek_id"
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    value={newRule.action}
                                    onChange={(e) => handleNewRuleChange('action', e.target.value)}
                                    placeholder="Omschrijving actie"
                                />
                            </TableCell>
                            {positions.map(pos => (
                                <TableCell key={pos} align="right">
                                    <TextField
                                        type="number"
                                        size="small"
                                        variant="outlined"
                                        value={newRule[pos]}
                                        onChange={(e) => handleNewRuleChange(pos, e.target.value)}
                                        sx={{ width: '80px' }}
                                    />
                                </TableCell>
                            ))}
                            <TableCell>
                                <Button onClick={handleAddRule} variant="contained" size="small">Toevoegen</Button>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveChanges}
                    disabled={saving}
                >
                    {saving ? <CircularProgress size={24} /> : 'Alle Wijzigingen Opslaan'}
                </Button>
            </Box>
        </Box>
    );
}

export default AdminPuntentelling;
